import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppScreen from '@/components/ui/AppScreen';
import PrimaryButton from '@/components/ui/PrimaryButton';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import {
  buscarTriboDetalhe,
  buscarMinhaTribo,
  abrirFolhaTribo,
  corRgba,
  COR_TRIBO_PADRAO,
  type TriboDetalhe,
} from '@/lib/tribo';

// Quebra um texto em parágrafos (linhas em branco separam) com boa leitura.
function Paragrafos({ texto }: { texto: string }) {
  const partes = texto
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <>
      {partes.map((p, i) => (
        <p
          key={i}
          className="mt-3 whitespace-pre-line text-[16px] leading-[1.6] text-foreground-700"
        >
          {p}
        </p>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// /tribo/:id — página da tribo: o lema, o nome, a história, o que é ser dela,
// uma oração e as curiosidades já descobertas (só na própria tribo), com as 13
// tribos no rodapé. Quando é a tribo da própria pessoa, um botão "Trocar"
// reabre a folha da tribo.
// ---------------------------------------------------------------------------
export default function TriboPage() {
  const navigate = useNavigate();
  const { triboId } = useParams();
  const id = Number(triboId);
  const { user, pronto } = useAuth();

  const [detalhe, setDetalhe] = useState<TriboDetalhe | null>(null);
  const [minhaId, setMinhaId] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!pronto) return;
    // Deslogado: nada de tribos.
    if (!user) {
      navigate('/', { replace: true });
      return;
    }
    let vivo = true;
    setCarregando(true);
    setErro(null);
    if (!Number.isFinite(id)) {
      setErro('Tribo não encontrada.');
      setCarregando(false);
      return;
    }
    (async () => {
      try {
        const d = await buscarTriboDetalhe(id);
        if (!vivo) return;
        // Quem não pode ver tribos é mandado de volta, sem nada sobre tribos.
        if (!d.permitido || !d.nome) {
          navigate('/', { replace: true });
          return;
        }
        setDetalhe(d);
        document.title = `${d.nome} · As tribos do Se Liga`;
        buscarMinhaTribo()
          .then((m) => {
            if (vivo) setMinhaId(m.tribo?.id ?? null);
          })
          .catch(() => {
            /* sem botão Trocar */
          });
      } catch (e) {
        if (vivo) setErro(mensagemAmigavel(e, 'Não foi possível carregar esta tribo.'));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [id, navigate, pronto, user]);

  // Ao sair da página, devolve o título padrão da aba (senão o nome da tribo
  // fica grudado nas outras telas).
  useEffect(() => {
    const anterior = document.title;
    return () => {
      document.title = anterior;
    };
  }, []);

  const cor = detalhe?.cor ?? COR_TRIBO_PADRAO;
  const ehMinha = !!detalhe && detalhe.minha && minhaId === detalhe.id;
  const historia = detalhe?.historia || detalhe?.resumo || '';

  return (
    <AppScreen>
      <main className="mx-auto w-full max-w-[640px] px-5 pb-16 pt-5">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="aperta flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap text-[15px] font-semibold text-foreground-600"
        >
          <i className="ri-arrow-left-line text-lg" aria-hidden />
          Voltar
        </button>

        {carregando && (
          <div className="mt-4 space-y-3">
            <div className="h-28 animate-pulse rounded-[22px] bg-background-200/70" />
            <div className="h-40 animate-pulse rounded-[18px] bg-background-200/70" />
            <div className="h-40 animate-pulse rounded-[18px] bg-background-200/70" />
          </div>
        )}

        {!carregando && erro && (
          <div className="mt-6 rounded-[18px] bg-danger-50 px-4 py-4 text-center">
            <p className="text-[15px] text-danger-700">{erro}</p>
            <Link
              to="/"
              className="aperta mt-3 inline-flex h-12 items-center justify-center whitespace-nowrap rounded-[16px] bg-primary-500 px-6 text-[15px] font-bold text-white"
            >
              Voltar ao início
            </Link>
          </div>
        )}

        {!carregando && detalhe && (
          <>
            {/* Cabeçalho com a cor da tribo */}
            <header
              className="app-entra mt-3 overflow-hidden rounded-[22px] p-5"
              style={{ backgroundColor: corRgba(cor, 0.14) }}
            >
              <span
                className="flex h-14 w-14 items-center justify-center rounded-full text-white shadow-pcard"
                style={{ backgroundColor: cor }}
              >
                <i className="ri-tent-line text-[28px]" aria-hidden />
              </span>
              <h1 className="mt-3 text-[26px] font-extrabold leading-tight text-foreground-950">
                {detalhe.nome}
              </h1>
              <p className="mt-1 text-[13px] font-semibold" style={{ color: corRgba(cor, 1) }}>
                Tribo do acampamento
              </p>
              {ehMinha && (
                <button
                  type="button"
                  onClick={() => abrirFolhaTribo()}
                  className="aperta mt-3 inline-flex h-10 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-background-50 px-4 text-[14px] font-semibold text-foreground-800 shadow-pcard"
                >
                  <i className="ri-refresh-line text-base" aria-hidden />
                  Trocar
                </button>
              )}
            </header>

            {/* Lema — citação destacada na cor da tribo */}
            {detalhe.lema && (
              <blockquote
                className="app-entra mt-4 rounded-r-[14px] py-1 pl-4 text-[17px] italic leading-[1.5] text-foreground-800"
                style={{ borderLeft: `4px solid ${cor}` }}
              >
                {detalhe.lema}
              </blockquote>
            )}

            {/* O nome */}
            {detalhe.significado && (
              <section className="app-entra mt-4 rounded-[18px] bg-background-50 p-5 shadow-pcard">
                <h2 className="text-[16px] font-extrabold text-foreground-950">O nome</h2>
                <p className="mt-2 text-[16px] leading-[1.6] text-foreground-700">
                  {detalhe.significado}
                </p>
              </section>
            )}

            {/* História */}
            {historia && (
              <section className="app-entra mt-4 rounded-[18px] bg-background-50 p-5 shadow-pcard">
                <h2 className="text-[16px] font-extrabold text-foreground-950">História</h2>
                <Paragrafos texto={historia} />
              </section>
            )}

            {/* O que é ser de {nome} */}
            {detalhe.reflexao && (
              <section className="app-entra mt-4 rounded-[18px] bg-background-50 p-5 shadow-pcard">
                <h2 className="text-[16px] font-extrabold text-foreground-950">
                  O que é ser de {detalhe.nome}
                </h2>
                <Paragrafos texto={detalhe.reflexao} />
              </section>
            )}

            {/* Para rezar */}
            {detalhe.oracao && (
              <section
                className="app-entra mt-4 rounded-[18px] p-5"
                style={{ backgroundColor: corRgba(cor, 0.1) }}
              >
                <h2 className="flex items-center gap-2 text-[16px] font-extrabold text-foreground-950">
                  <i className="ri-heart-line text-lg" style={{ color: corRgba(cor, 1) }} aria-hidden />
                  Para rezar
                </h2>
                <Paragrafos texto={detalhe.oracao} />
                {detalhe.lema && (
                  <p className="mt-3 text-[15px] font-bold text-foreground-900">{detalhe.lema}</p>
                )}
              </section>
            )}

            {/* Curiosidades — só na própria tribo e só as já descobertas */}
            {detalhe.minha && detalhe.curiosidades.length > 0 && (
              <section className="app-entra mt-4 rounded-[18px] bg-background-50 p-5 shadow-pcard">
                <h2 className="text-[16px] font-extrabold text-foreground-950">
                  Curiosidades que você já descobriu
                </h2>
                <p className="mt-1 text-[13px] text-foreground-400">
                  {detalhe.curiosidades_vistas} de {detalhe.curiosidades_total} · toda semana, no
                  domingo, chega uma nova
                </p>
                <ul className="mt-3 space-y-3">
                  {detalhe.curiosidades.map((c, i) => (
                    <li key={i} className="flex gap-3">
                      <span
                        className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: cor }}
                        aria-hidden
                      />
                      <div className="min-w-0">
                        <p className="text-[15px] leading-relaxed text-foreground-800">{c.texto}</p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                          {c.ref && (
                            <span className="text-[12px] text-foreground-400">{c.ref}</span>
                          )}
                          {c.desta_semana && (
                            <span className="inline-flex items-center rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-bold text-accent-900">
                              Desta semana
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* As 13 tribos */}
            {detalhe.todas.length > 0 && (
              <section className="app-entra mt-6">
                <h2 className="text-[16px] font-extrabold text-foreground-950">As 13 tribos</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {detalhe.todas.map((t) => {
                    const c = t.cor ?? COR_TRIBO_PADRAO;
                    const atual = t.id === detalhe.id;
                    return (
                      <Link
                        key={t.id}
                        to={`/tribo/${t.id}`}
                        className={`aperta inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-[13px] font-semibold text-foreground-950 transition-colors ${
                          atual ? 'border-2' : 'border border-background-200'
                        }`}
                        style={{
                          backgroundColor: corRgba(c, atual ? 0.22 : 0.1),
                          borderColor: atual ? c : undefined,
                        }}
                      >
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: c }}
                          aria-hidden
                        />
                        {t.nome}
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {ehMinha && (
              <PrimaryButton
                cheio
                className="mt-6"
                icone="ri-refresh-line"
                onClick={() => abrirFolhaTribo()}
              >
                Trocar de tribo
              </PrimaryButton>
            )}
          </>
        )}
      </main>
    </AppScreen>
  );
}